import { useState, useEffect, useCallback } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import { X, Save, Loader2, Download, Mail } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { PDFDocument, PDFTextField, PDFCheckBox, PDFDropdown, PDFRadioGroup } from "pdf-lib";

import "react-pdf/dist/esm/Page/AnnotationLayer.css";
import "react-pdf/dist/esm/Page/TextLayer.css";

// Worker local (precisa existir em /public/pdf.worker.min.js) em vez de um
// CDN externo — evita bloqueio por política de segurança (CSP) do site.
pdfjs.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL || ""}/pdf.worker.min.js`;

// Mesmo padrão de axios usado em Forms.jsx e AdminForms.jsx — evita o bug
// de prefixo /api duplicado que vinha de usar um axios compartilhado diferente.
const API = axios.create({ baseURL: "https://webapp-sitesync.onrender.com" });
const _token = typeof window !== "undefined" ? localStorage.getItem("ss_token") : null;
if (_token) {
  API.defaults.headers.common["Authorization"] = `Bearer ${_token}`;
}

/**
 * Compara dois PDFRef do pdf-lib pelo número do objeto + geração, para achar
 * em qual página (0-based) um widget de campo está desenhado.
 */
function findPageIndexForRef(pageRefs, ref) {
  if (!ref) return 0;
  return Math.max(
    0,
    pageRefs.findIndex((r) => r.objectNumber === ref.objectNumber && r.generationNumber === ref.generationNumber)
  );
}

/**
 * Lê o AcroForm do PDF (via pdf-lib) e devolve, para cada WIDGET (não para
 * cada campo — um campo pode ter vários widgets, ex: um checkbox com várias
 * opções), a página e o retângulo exatos onde ele deve ser desenhado, em
 * pontos do PDF (origem no canto inferior-esquerdo da página).
 */
function extractFieldOverlays(pdfDoc) {
  const form = pdfDoc.getForm();
  const pages = pdfDoc.getPages();
  const pageRefs = pages.map((p) => p.ref);
  const pageSizes = pages.map((p) => p.getSize());

  const overlays = [];
  const initialValues = {};

  for (const field of form.getFields()) {
    const name = field.getName();
    let type = "text";
    let options = null;

    if (field instanceof PDFCheckBox) {
      type = "checkbox";
      initialValues[name] = field.isChecked();
    } else if (field instanceof PDFDropdown) {
      type = "dropdown";
      options = field.getOptions();
      initialValues[name] = field.getSelected()[0] || "";
    } else if (field instanceof PDFRadioGroup) {
      type = "radio";
      options = field.getOptions();
      initialValues[name] = field.getSelected() || "";
    } else if (field instanceof PDFTextField) {
      type = "text";
      initialValues[name] = field.getText() || "";
    } else {
      continue; // tipo de campo não suportado (ex: botão de assinatura/push button)
    }

    let widgets = [];
    try {
      widgets = field.acroField.getWidgets();
    } catch (e) {
      continue;
    }

    widgets.forEach((widget, widgetIndex) => {
      let rect;
      try {
        rect = widget.getRectangle();
      } catch (e) {
        return;
      }
      const pageIndex = findPageIndexForRef(pageRefs, widget.P());
      const pageSize = pageSizes[pageIndex] || { width: 612, height: 792 };

      overlays.push({
        id: `${name}__${widgetIndex}`,
        fieldName: name,
        type,
        options,
        pageIndex,
        pageWidth: pageSize.width,
        pageHeight: pageSize.height,
        rect, // { x, y, width, height } em pontos, origem inferior-esquerda
        // Para checkbox/radio com múltiplos widgets, cada widget normalmente
        // representa UMA opção. Tentamos casar pela ordem das opções; se não
        // houver options (checkbox simples), não se aplica.
        optionValue: options ? options[widgetIndex] ?? options[0] : null,
      });
    });
  }

  return { overlays, initialValues };
}

/**
 * formId       -> id do template em /forms (sempre necessário)
 * submissionId -> se vier preenchido, abre um preenchimento JÁ SALVO para EDITAR
 *                 (sobrescreve o mesmo registro). Se vier null/undefined, é a
 *                 primeira vez que o worker preenche (vai CRIAR um novo registro).
 * formName, projectId, onClose, onSave -> iguais a antes
 */
export default function PDFViewer({ formId, formName, projectId, submissionId: initialSubmissionId, onClose, onSave }) {
  const [numPages, setNumPages] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [scale, setScale] = useState(1.2);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [pdfBlobUrl, setPdfBlobUrl] = useState(null);
  const [originalBytes, setOriginalBytes] = useState(null);
  const [overlays, setOverlays] = useState([]); // posição de cada widget, por página
  const [fieldValues, setFieldValues] = useState({}); // fieldName -> valor atual
  const [currentSubmissionId, setCurrentSubmissionId] = useState(initialSubmissionId || null);

  useEffect(() => {
    let objectUrl = null;

    const fetchPdf = async () => {
      setLoading(true);
      setLoadError(null);
      try {
        let response;
        if (currentSubmissionId) {
          response = await API.get(`/api/forms/submissions/${currentSubmissionId}/download`, { responseType: "blob" });
        } else {
          try {
            response = await API.get(`/api/forms/${formId}/download`, { responseType: "blob" });
          } catch (e1) {
            response = await API.get(`/api/forms/${formId}/file`, { responseType: "blob" });
          }
        }

        const arrayBuffer = await response.data.arrayBuffer();
        setOriginalBytes(arrayBuffer);

        const blob = new Blob([arrayBuffer], { type: "application/pdf" });
        objectUrl = URL.createObjectURL(blob);
        setPdfBlobUrl(objectUrl);

        await loadFormFields(arrayBuffer);
      } catch (error) {
        console.error("Failed to download PDF blob:", error);
        const status = error?.response?.status;
        if (status === 404) {
          setLoadError(
            "This PDF file could not be found on the server. It may have been removed during a recent deploy — try re-uploading it from the Admin Forms screen."
          );
        } else {
          setLoadError("Failed to load this form. Please try again.");
        }
        toast.error("Form file not found on server.");
      } finally {
        setLoading(false);
      }
    };

    if (formId || currentSubmissionId) {
      fetchPdf();
    }

    return () => {
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formId, currentSubmissionId]);

  const loadFormFields = async (arrayBuffer) => {
    try {
      const pdfDoc = await PDFDocument.load(arrayBuffer, { ignoreEncryption: true });
      const { overlays: ov, initialValues } = extractFieldOverlays(pdfDoc);
      setOverlays(ov);
      setFieldValues(initialValues);
    } catch (err) {
      console.warn("This PDF has no fillable form fields:", err.message);
      setOverlays([]);
      setFieldValues({});
    }
  };

  const updateFieldValue = (fieldName, value) => {
    setFieldValues((prev) => ({ ...prev, [fieldName]: value }));
  };

  const buildFilledPdfBytes = useCallback(async () => {
    if (!originalBytes) return null;
    const pdfDoc = await PDFDocument.load(originalBytes, { ignoreEncryption: true });
    const form = pdfDoc.getForm();

    for (const [name, value] of Object.entries(fieldValues)) {
      try {
        const field = form.getField(name);
        if (field instanceof PDFTextField) {
          field.setText(value || "");
        } else if (field instanceof PDFCheckBox) {
          value ? field.check() : field.uncheck();
        } else if (field instanceof PDFDropdown) {
          if (value) field.select(value);
        } else if (field instanceof PDFRadioGroup) {
          if (value) field.select(value);
        }
      } catch (e) {
        console.warn(`Could not set field ${name}:`, e.message);
      }
    }

    return pdfDoc.save();
  }, [originalBytes, fieldValues]);

  const handleSaveFilled = async () => {
    setSaving(true);
    try {
      const pdfBytes = await buildFilledPdfBytes();
      if (!pdfBytes) throw new Error("PDF not loaded");

      const filledBlob = new Blob([pdfBytes], { type: "application/pdf" });
      const formData = new FormData();
      formData.append("file", filledBlob, `${formName || "form"}.pdf`);

      if (currentSubmissionId) {
        await API.put(`/api/forms/submissions/${currentSubmissionId}`, formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        toast.success("Form updated!");
      } else {
        const res = await API.post(`/api/forms/${formId}/submit`, formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        setCurrentSubmissionId(res.data.id);
        toast.success("Form saved!");
      }

      const refreshedBlob = new Blob([pdfBytes], { type: "application/pdf" });
      setPdfBlobUrl(URL.createObjectURL(refreshedBlob));
      setOriginalBytes(await refreshedBlob.arrayBuffer());

      onSave?.();
    } catch (error) {
      console.error("Error saving filled PDF:", error);
      toast.error("Failed to save form.");
    } finally {
      setSaving(false);
    }
  };

  const handleDownload = async () => {
    try {
      const pdfBytes = await buildFilledPdfBytes();
      const blob = new Blob([pdfBytes || originalBytes], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${formName || "form"}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Download error:", error);
      toast.error("Failed to download PDF.");
    }
  };

  const handleEmail = async () => {
    const to = window.prompt("Send this form to which email address?");
    if (!to) return;

    setSendingEmail(true);
    try {
      let subId = currentSubmissionId;
      if (!subId) {
        const pdfBytes = await buildFilledPdfBytes();
        const filledBlob = new Blob([pdfBytes], { type: "application/pdf" });
        const formData = new FormData();
        formData.append("file", filledBlob, `${formName || "form"}.pdf`);
        const res = await API.post(`/api/forms/${formId}/submit`, formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        subId = res.data.id;
        setCurrentSubmissionId(subId);
      }

      await API.post(`/api/forms/submissions/${subId}/email`, { to_email: to });
      toast.success(`Form sent to ${to}`);
    } catch (error) {
      console.error("Email error:", error);
      toast.error("Failed to send email.");
    } finally {
      setSendingEmail(false);
    }
  };

  // Overlays da página atualmente visível
  const pageOverlays = overlays.filter((o) => o.pageIndex === pageNumber - 1);
  const hasAnyFields = overlays.length > 0;

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex flex-col z-50">
      {/* Header */}
      <div className="bg-zinc-950 border-b border-zinc-800 px-6 py-4 flex justify-between items-center">
        <div>
          <h2 className="text-lg font-semibold text-white tracking-tight">{formName}</h2>
          {currentSubmissionId && (
            <p className="text-xs text-zinc-500 font-mono mt-0.5">Editing saved submission</p>
          )}
        </div>
        <button onClick={onClose} className="p-2 hover:bg-zinc-800 rounded-md transition text-zinc-400 hover:text-white">
          <X size={20} />
        </button>
      </div>

      {/* Body: PDF com os campos preenchíveis desenhados em cima, na posição real */}
      <div className="flex-1 overflow-auto bg-zinc-900 flex flex-col items-center justify-start gap-4 py-8 px-8 relative">
        {loading ? (
          <div className="flex flex-col items-center justify-center h-full text-zinc-300 gap-3">
            <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
            <p className="text-sm font-mono text-zinc-500">Loading document...</p>
          </div>
        ) : loadError ? (
          <div className="flex flex-col items-center justify-center h-full gap-3 px-8 text-center max-w-md">
            <div className="w-12 h-12 rounded-full bg-red-500/10 flex items-center justify-center">
              <X size={24} className="text-red-400" />
            </div>
            <p className="text-sm text-zinc-300">{loadError}</p>
          </div>
        ) : pdfBlobUrl ? (
          <>
            {!hasAnyFields && (
              <div className="w-full max-w-2xl bg-amber-500/10 border border-amber-500/30 text-amber-300 text-xs rounded-md px-4 py-2.5">
                This PDF has no fillable form fields — it was likely uploaded as a flat/static PDF rather than a
                true fillable form. You can still download or email it as-is.
              </div>
            )}
            <div className="shadow-2xl rounded-sm overflow-hidden mx-auto w-fit relative">
              <Document
                file={pdfBlobUrl}
                onLoadSuccess={({ numPages }) => setNumPages(numPages)}
                onLoadError={(error) => {
                  console.error("PDF load error:", error);
                  toast.error("Failed to render PDF: " + error.message);
                }}
              >
                <div className="relative">
                  <Page pageNumber={pageNumber} scale={scale} renderAnnotationLayer={false} renderTextLayer={false} />

                  {/* Overlay: um input/checkbox/select por cima da posição real do campo */}
                  {pageOverlays.map((o) => {
                    // Converte pontos do PDF (origem inferior-esquerda) para
                    // pixels de tela (origem superior-esquerda), na escala atual.
                    const left = o.rect.x * scale;
                    const top = (o.pageHeight - o.rect.y - o.rect.height) * scale;
                    const width = o.rect.width * scale;
                    const height = o.rect.height * scale;
                    const style = { position: "absolute", left, top, width, height };

                    if (o.type === "text") {
                      return (
                        <input
                          key={o.id}
                          type="text"
                          value={fieldValues[o.fieldName] ?? ""}
                          onChange={(e) => updateFieldValue(o.fieldName, e.target.value)}
                          style={{ ...style, fontSize: Math.max(10, height * 0.6) }}
                          className="bg-blue-500/10 border border-blue-500/60 focus:border-blue-400 focus:bg-blue-500/20 outline-none px-1 text-zinc-900 font-medium rounded-[2px]"
                        />
                      );
                    }

                    if (o.type === "checkbox") {
                      return (
                        <input
                          key={o.id}
                          type="checkbox"
                          checked={!!fieldValues[o.fieldName]}
                          onChange={(e) => updateFieldValue(o.fieldName, e.target.checked)}
                          style={{ ...style, accentColor: "#2563eb" }}
                          className="cursor-pointer"
                        />
                      );
                    }

                    if (o.type === "radio") {
                      return (
                        <input
                          key={o.id}
                          type="radio"
                          name={o.fieldName}
                          value={o.optionValue}
                          checked={fieldValues[o.fieldName] === o.optionValue}
                          onChange={() => updateFieldValue(o.fieldName, o.optionValue)}
                          style={{ ...style, accentColor: "#2563eb" }}
                          className="cursor-pointer"
                        />
                      );
                    }

                    if (o.type === "dropdown") {
                      return (
                        <select
                          key={o.id}
                          value={fieldValues[o.fieldName] ?? ""}
                          onChange={(e) => updateFieldValue(o.fieldName, e.target.value)}
                          style={{ ...style, fontSize: Math.max(10, height * 0.6) }}
                          className="bg-blue-500/10 border border-blue-500/60 focus:border-blue-400 outline-none text-zinc-900 font-medium rounded-[2px]"
                        >
                          <option value="">—</option>
                          {(o.options || []).map((opt) => (
                            <option key={opt} value={opt}>
                              {opt}
                            </option>
                          ))}
                        </select>
                      );
                    }

                    return null;
                  })}
                </div>
              </Document>
            </div>
          </>
        ) : null}
      </div>

      {/* Controls */}
      <div className="bg-zinc-950 border-t border-zinc-800 px-6 py-4 space-y-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 text-zinc-300">
            <button
              onClick={() => setPageNumber(Math.max(1, pageNumber - 1))}
              disabled={pageNumber === 1}
              className="px-3 py-1.5 border border-zinc-700 rounded-md hover:bg-zinc-800 disabled:opacity-30 text-sm transition"
            >
              ← Prev
            </button>
            <span className="text-xs font-mono text-zinc-500 w-20 text-center">
              Page {pageNumber} / {numPages || "?"}
            </span>
            <button
              onClick={() => setPageNumber(Math.min(numPages || 1, pageNumber + 1))}
              disabled={pageNumber === numPages}
              className="px-3 py-1.5 border border-zinc-700 rounded-md hover:bg-zinc-800 disabled:opacity-30 text-sm transition"
            >
              Next →
            </button>
          </div>

          <div className="flex items-center gap-2 text-zinc-300">
            <button onClick={() => setScale(Math.max(0.5, scale - 0.2))} className="px-3 py-1.5 border border-zinc-700 rounded-md hover:bg-zinc-800 text-sm transition">
              −
            </button>
            <span className="text-xs font-mono text-zinc-500 w-12 text-center">{Math.round(scale * 100)}%</span>
            <button onClick={() => setScale(Math.min(2, scale + 0.2))} className="px-3 py-1.5 border border-zinc-700 rounded-md hover:bg-zinc-800 text-sm transition">
              +
            </button>
          </div>
        </div>

        <div className="flex gap-2">
          <button
            onClick={handleSaveFilled}
            disabled={saving || loading || !!loadError}
            className="flex-1 bg-emerald-600 hover:bg-emerald-500 text-white py-2.5 rounded-md flex items-center justify-center gap-2 text-sm font-medium transition disabled:opacity-40"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save size={16} />}
            {currentSubmissionId ? "Save Changes" : "Save Form"}
          </button>
          <button
            onClick={handleDownload}
            disabled={loading || !!loadError}
            className="flex-1 bg-zinc-800 hover:bg-zinc-700 text-white py-2.5 rounded-md flex items-center justify-center gap-2 text-sm font-medium transition disabled:opacity-40"
          >
            <Download size={16} /> Download
          </button>
          <button
            onClick={handleEmail}
            disabled={sendingEmail || loading || !!loadError}
            className="flex-1 bg-zinc-800 hover:bg-zinc-700 text-white py-2.5 rounded-md flex items-center justify-center gap-2 text-sm font-medium transition disabled:opacity-40"
          >
            {sendingEmail ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail size={16} />} Email
          </button>
          <button onClick={onClose} className="px-6 bg-transparent border border-zinc-700 hover:bg-zinc-800 text-zinc-300 py-2.5 rounded-md text-sm font-medium transition">
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
